/**
 * Join service names for toasts/banners; truncate long lists.
 * Example: ["A","B","C","D"] with maxVisible=2 → "A, B +2"
 */
export function formatJoinedNames(
  names: string[],
  maxVisible = 2
): { text: string; truncated: boolean; hiddenCount: number } {
  const cleaned = names.map((n) => n.trim()).filter(Boolean);

  if (cleaned.length <= maxVisible) {
    return {
      text: cleaned.join(", "),
      truncated: false,
      hiddenCount: 0,
    };
  }

  const visible = cleaned.slice(0, maxVisible).join(", ");
  const hiddenCount = cleaned.length - maxVisible;

  return {
    text: `${visible} +${hiddenCount}`,
    truncated: true,
    hiddenCount,
  };
}

export function moveIdInOrder(
  ids: number[],
  index: number,
  direction: -1 | 1
): number[] {
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) {
    return ids;
  }
  const next = [...ids];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

/** Move item from `fromIndex` to `toIndex` (for drag-and-drop). */
export function reorderIds(
  ids: number[],
  fromIndex: number,
  toIndex: number
): number[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= ids.length ||
    toIndex >= ids.length
  ) {
    return ids;
  }
  const next = [...ids];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

/**
 * Reorder `selectedIds` so known ids follow `preferred`, then append the rest
 * in their current relative order.
 */
export function applyPreferredOrder(
  selectedIds: number[],
  preferred: number[] | null | undefined
): number[] {
  if (!preferred?.length || selectedIds.length === 0) {
    return selectedIds;
  }
  const selected = new Set(selectedIds);
  const ordered = preferred.filter((id) => selected.has(id));
  const rest = selectedIds.filter((id) => !preferred.includes(id));
  return [...ordered, ...rest];
}

/** True when preferred order would change the current selection sequence. */
export function differsFromPreferredOrder(
  selectedIds: number[],
  preferred: number[] | null | undefined
): boolean {
  if (!preferred?.length || selectedIds.length < 2) {
    return false;
  }
  const overlap = preferred.filter((id) => selectedIds.includes(id));
  if (overlap.length < 2) {
    return false;
  }
  const applied = applyPreferredOrder(selectedIds, preferred);
  return applied.some((id, index) => id !== selectedIds[index]);
}
